import { admin2, orgAdmin } from '@/api/instance'

/**
 * 取得第三方交易紀錄，所有 source 選項
 * admin.memberExternalTransaction.getSourceOptions
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店ID
 * @returns {import('@/api/instance').Api2Response<Array>} 來源選項列表
 */
export const GetMemberExternalTransactionSourceOptions = async ({ shopId }) => {
  return await admin2({
    method: 'GET',
    url: `/${shopId}/memberExternalTransaction/sourceOptions`,
  })
}
/**
 * 取得第三方消費紀錄列表 admin.memberExternalTransaction.find
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店ID
 * @param {string} [params.source] - 來源
 * @param {string} [params.transactionId] - 交易編號（模糊搜尋）
 * @param {string} [params.exactTransactionId] - 交易編號（精確搜尋）
 * @param {string} [params.transactionTimeStart] - 交易時間起始
 * @param {string} [params.transactionTimeEnd] - 交易時間結束
 * @param {number} [params.start=0] - 查詢起始
 * @param {number} [params.limit=20] - 查詢筆數
 * @param {string} [params.customerPhone] - 顧客電話
 * @param {Array} [params.branchIds] - 分店ID列表
 * @param {string} [params.MemberId] - 會員ID
 * @returns {import('@/api/instance').Api2Response<Array>} 第三方消費紀錄列表
 */
export const GetMemberExternalTransaction = async ({
  shopId,
  source,
  transactionId,
  exactTransactionId,
  transactionTimeStart,
  transactionTimeEnd,
  start,
  limit,
  customerPhone,
  branchIds,
  MemberId,
}) => {
  return await admin2({
    method: 'GET',
    url: `/${shopId}/memberExternalTransaction`,
    params: {
      source,
      transactionId,
      exactTransactionId,
      transactionTimeStart,
      transactionTimeEnd,
      start,
      limit,
      customerPhone,
      branchIds,
      MemberId,
    },
  })
}

/**
 * 取得第三方消費紀錄總數 admin.memberExternalTransaction.count
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店ID
 * @param {string} [params.source] - 來源
 * @param {string} [params.transactionId] - 交易編號（模糊搜尋）
 * @param {string} [params.exactTransactionId] - 交易編號（精確搜尋）
 * @param {string} [params.transactionTimeStart] - 交易時間起始
 * @param {string} [params.transactionTimeEnd] - 交易時間結束
 * @param {string} [params.itemName] - 品項名稱
 * @param {string} [params.itemNo] - 品項編號
 * @param {string} [params.fuzzySearchItemName] - 品項名稱（模糊搜尋）
 * @param {string} [params.customerPhone] - 顧客電話
 * @param {Array} [params.branchIds] - 分店ID列表
 * @param {string} [params.MemberId] - 會員ID
 * @returns {import('@/api/instance').Api2Response<{ count: number }>} 總筆數
 */
export const GetMemberExternalTransactionCount = async ({
  shopId,
  source,
  transactionId,
  exactTransactionId,
  transactionTimeStart,
  transactionTimeEnd,
  itemName,
  itemNo,
  fuzzySearchItemName,
  customerPhone,
  branchIds,
  MemberId,
}) => {
  return await admin2({
    method: 'GET',
    url: `/${shopId}/memberExternalTransaction/count`,
    params: {
      source,
      transactionId,
      exactTransactionId,
      transactionTimeStart,
      transactionTimeEnd,
      itemName,
      itemNo,
      fuzzySearchItemName,
      customerPhone,
      branchIds,
      MemberId,
    },
  })
}

/**
 * 查詢單筆第三方消費紀錄 admin.memberExternalTransaction.findOne
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店ID
 * @param {string} params.id - 第三方消費紀錄ID
 * @returns {import('@/api/instance').Api2Response} 第三方消費紀錄
 */
export const FindMemberExternalTransaction = async ({
  shopId,
  id,
}) => {
  return await admin2({
    method: 'GET',
    url: `/${shopId}/memberExternalTransaction/${id}`,
  })
}

/**
 * 作廢或退款第三方消費紀錄 admin.memberExternalTransaction.manualVoidOrRefund
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店ID
 * @param {string} params.id - 第三方消費紀錄ID
 * @param {string} params.type - 操作類型 (void|refund)
 * @param {string} params.reason - 原因
 * @param {Array} [params.items] - 退款品項列表
 * @returns {import('@/api/instance').Api2Response} 操作結果
 */
export const VoidOrRefundMemberExternalTransaction = async ({
  shopId,
  id,
  type,
  reason,
  items,
}) => {
  return await admin2({
    method: 'POST',
    url: `/${shopId}/memberExternalTransaction/${id}/voidOrRefund`,
    data: { type, reason, items },
  })
}

// scope: orgAdmin
/**
 * 取得第三方消費來源選項 orgAdmin.memberExternalTransaction.getSourceOptions
 * @param {Object} params - 查詢參數
 * @param {string} params.orgId - 組織ID
 * @returns {import('@/api/instance').Api2Response<Array>} 來源選項列表
 */
export const OrgAdminGetMemberExternalTransactionSourceOptions = async ({ orgId }) => {
  return await orgAdmin({
    method: 'GET',
    url: `/${orgId}/memberExternalTransaction/sourceOptions`,
  })
}
/**
 * 取得第三方消費紀錄列表 orgAdmin.memberExternalTransaction.find
 * @param {Object} params - 查詢參數
 * @param {string} params.orgId - 組織ID
 * @param {string} [params.source] - 來源
 * @param {string} [params.transactionId] - 交易編號（模糊搜尋）
 * @param {string} [params.transactionTimeStart] - 交易時間起始
 * @param {string} [params.transactionTimeEnd] - 交易時間結束
 * @param {number} [params.start=0] - 查詢起始
 * @param {number} [params.limit=20] - 查詢筆數
 * @param {string} [params.customerPhone] - 顧客電話
 * @param {Array} [params.shopIds] - 商店ID列表
 * @param {string} [params.MemberId] - 會員ID
 * @returns {import('@/api/instance').Api2Response<Array>} 第三方消費紀錄列表
 */
export const OrgAdminGetMemberExternalTransaction = async ({
  orgId,
  source,
  transactionId,
  transactionTimeStart,
  transactionTimeEnd,
  start,
  limit,
  customerPhone,
  shopIds,
  MemberId,
}) => {
  return await orgAdmin({
    method: 'GET',
    url: `/${orgId}/memberExternalTransaction`,
    params: {
      source,
      transactionId,
      transactionTimeStart,
      transactionTimeEnd,
      start,
      limit,
      customerPhone,
      shopIds,
      MemberId,
    },
  })
}

/**
 * 取得第三方消費紀錄總數 orgAdmin.memberExternalTransaction.count
 * @param {Object} params - 查詢參數
 * @param {string} params.orgId - 組織ID
 * @param {string} [params.source] - 來源
 * @param {string} [params.transactionId] - 交易編號（模糊搜尋）
 * @param {string} [params.transactionTimeStart] - 交易時間起始
 * @param {string} [params.transactionTimeEnd] - 交易時間結束
 * @param {string} [params.itemName] - 品項名稱
 * @param {string} [params.fuzzySearchItemName] - 品項名稱（模糊搜尋）
 * @param {string} [params.customerPhone] - 顧客電話
 * @param {Array} [params.shopIds] - 商店ID列表
 * @param {string} [params.MemberId] - 會員ID
 * @returns {import('@/api/instance').Api2Response<{ count: number }>} 總筆數
 */
export const OrgAdminGetMemberExternalTransactionCount = async ({
  orgId,
  source,
  transactionId,
  transactionTimeStart,
  transactionTimeEnd,
  itemName,
  fuzzySearchItemName,
  customerPhone,
  shopIds,
  MemberId,
}) => {
  return await orgAdmin({
    method: 'GET',
    url: `/${orgId}/memberExternalTransaction/count`,
    params: {
      source,
      transactionId,
      transactionTimeStart,
      transactionTimeEnd,
      itemName,
      fuzzySearchItemName,
      customerPhone,
      shopIds,
      MemberId,
    },
  })
}

/**
 * 查詢單筆第三方消費紀錄 orgAdmin.memberExternalTransaction.findOne
 * @param {Object} params - 查詢參數
 * @param {string} params.orgId - 組織ID
 * @param {string} params.id - 第三方消費紀錄ID
 * @returns {import('@/api/instance').Api2Response<Object>} 第三方消費紀錄
 */
export const OrgAdminFindMemberExternalTransaction = async ({
  orgId,
  id,
}) => {
  return await orgAdmin({
    method: 'GET',
    url: `/${orgId}/memberExternalTransaction/${id}`,
  })
}

/**
 * 取得組織第三方消費來源選項 admin.orgExternalTransaction.getSourceOptions
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店ID
 * @returns {import('@/api/instance').Api2Response<Array>} 來源選項列表
 */
export const GetOrgExternalTransactionSourceOptions = async ({ shopId }) => {
  return await admin2({
    method: 'GET',
    url: `/${shopId}/orgExternalTransaction/sourceOptions`,
  })
}

/**
 * 取得組織第三方消費紀錄列表 admin.orgExternalTransaction.find
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店ID
 * @param {string} [params.source] - 來源
 * @param {string} [params.transactionId] - 交易編號（模糊搜尋）
 * @param {string} [params.exactTransactionId] - 交易編號（精確搜尋）
 * @param {string} [params.transactionTimeStart] - 交易時間起始
 * @param {string} [params.transactionTimeEnd] - 交易時間結束
 * @param {number} [params.start=0] - 查詢起始
 * @param {number} [params.limit=20] - 查詢筆數
 * @param {string} [params.customerPhone] - 顧客電話
 * @param {string} [params.MemberId] - 會員ID
 * @returns {import('@/api/instance').Api2Response<Array>} 組織第三方消費紀錄列表
 */
export const GetOrgExternalTransaction = async ({
  shopId,
  source,
  transactionId,
  exactTransactionId,
  transactionTimeStart,
  transactionTimeEnd,
  start,
  limit,
  customerPhone,
  MemberId,
}) => {
  return await admin2({
    method: 'GET',
    url: `/${shopId}/orgExternalTransaction`,
    params: {
      source,
      transactionId,
      exactTransactionId,
      transactionTimeStart,
      transactionTimeEnd,
      start,
      limit,
      customerPhone,
      MemberId,
    },
  })
}

/**
 * 取得組織第三方消費紀錄總數 admin.orgExternalTransaction.count
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店ID
 * @param {string} [params.source] - 來源
 * @param {string} [params.transactionId] - 交易編號（模糊搜尋）
 * @param {string} [params.exactTransactionId] - 交易編號（精確搜尋）
 * @param {string} [params.transactionTimeStart] - 交易時間起始
 * @param {string} [params.transactionTimeEnd] - 交易時間結束
 * @param {string} [params.itemName] - 品項名稱
 * @param {string} [params.fuzzySearchItemName] - 品項名稱（模糊搜尋）
 * @param {string} [params.customerPhone] - 顧客電話
 * @param {string} [params.MemberId] - 會員ID
 * @returns {import('@/api/instance').Api2Response<{ count: number }>} 總筆數
 */
export const GetOrgExternalTransactionCount = async ({
  shopId,
  source,
  transactionId,
  exactTransactionId,
  transactionTimeStart,
  transactionTimeEnd,
  itemName,
  fuzzySearchItemName,
  customerPhone,
  MemberId,
}) => {
  return await admin2({
    method: 'GET',
    url: `/${shopId}/orgExternalTransaction/count`,
    params: {
      source,
      transactionId,
      exactTransactionId,
      transactionTimeStart,
      transactionTimeEnd,
      itemName,
      fuzzySearchItemName,
      customerPhone,
      MemberId,
    },
  })
}

/**
 * 查詢單筆組織第三方消費紀錄 admin.orgExternalTransaction.findOne
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店ID
 * @param {string} params.id - 第三方消費紀錄ID
 * @returns {import('@/api/instance').Api2Response<Object>} 組織第三方消費紀錄
 */
export const FindOrgExternalTransaction = async ({
  shopId,
  id,
}) => {
  return await admin2({
    method: 'GET',
    url: `/${shopId}/orgExternalTransaction/${id}`,
  })
}

/**
 * 取得總部第三方消費來源選項 admin.headquarterExternalTransaction.getSourceOptions
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店ID
 * @returns {import('@/api/instance').Api2Response<Array>} 來源選項列表
 */
export const GetHeadquarterExternalTransactionSourceOptions = async ({ shopId }) => {
  return await admin2({
    method: 'GET',
    url: `/${shopId}/headquarterExternalTransaction/sourceOptions`,
  })
}

/**
 * 取得總部第三方消費紀錄列表 admin.headquarterExternalTransaction.find
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店ID
 * @param {string} [params.source] - 來源
 * @param {string} [params.transactionId] - 交易編號（模糊搜尋）
 * @param {string} [params.transactionTimeStart] - 交易時間起始
 * @param {string} [params.transactionTimeEnd] - 交易時間結束
 * @param {number} [params.start=0] - 查詢起始
 * @param {number} [params.limit=20] - 查詢筆數
 * @param {string} [params.customerPhone] - 顧客電話
 * @param {string} [params.itemName] - 品項名稱
 * @param {string} [params.fuzzySearchItemName] - 品項名稱（模糊搜尋）
 * @param {string} [params.MemberId] - 會員ID
 * @returns {import('@/api/instance').Api2Response<Array>} 總部第三方消費紀錄列表
 */
export const GetHeadquarterExternalTransaction = async ({
  shopId,
  source,
  transactionId,
  transactionTimeStart,
  transactionTimeEnd,
  start,
  limit,
  customerPhone,
  itemName,
  fuzzySearchItemName,
  MemberId,
}) => {
  return await admin2({
    method: 'GET',
    url: `/${shopId}/headquarterExternalTransaction`,
    params: {
      source,
      transactionId,
      transactionTimeStart,
      transactionTimeEnd,
      start,
      limit,
      customerPhone,
      itemName,
      fuzzySearchItemName,
      MemberId,
    },
  })
}

/**
 * 取得總部第三方消費紀錄總數 admin.headquarterExternalTransaction.count
 * @param {Object} params - 查詢參數
 * @returns {import('@/api/instance').Api2Response<{ count: number }>} 總筆數
 */
export const GetHeadquarterExternalTransactionCount = async (params) => {
  return await admin2({
    method: 'GET',
    url: `/${params.shopId}/headquarterExternalTransaction/count`,
    params: {
      source: params.source,
      transactionId: params.transactionId,
      transactionTimeStart: params.transactionTimeStart,
      transactionTimeEnd: params.transactionTimeEnd,
      customerPhone: params.customerPhone,
      itemName: params.itemName,
      fuzzySearchItemName: params.fuzzySearchItemName,
      MemberId: params.MemberId,
    },
  })
}

/**
 * 查詢單筆總部第三方消費紀錄 admin.headquarterExternalTransaction.findOne
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店ID
 * @param {string} params.id - 第三方消費紀錄ID
 * @returns {import('@/api/instance').Api2Response<Object>} 總部第三方消費紀錄
 */
export const FindHeadquarterExternalTransaction = async ({ shopId, id }) => {
  return await admin2({
    method: 'GET',
    url: `/${shopId}/headquarterExternalTransaction/${id}`,
  })
}

/**
 * 標記第三方消費紀錄為作廢 admin.memberExternalTransaction.addInvalidLabel
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店ID
 * @param {string} params.id - 第三方消費紀錄ID
 * @returns {import('@/api/instance').Api2Response<Object>} 操作結果
 */
export const VoidMemberExternalTransaction = async ({ shopId, id }) => {
  return await admin2({
    method: 'PUT',
    url: `/${shopId}/memberExternalTransaction/${id}/addInvalidLabel`,
  })
}

/**
 * 查詢事件行銷獎勵發放紀錄 admin.memberExternalTransaction.findEventPlaybookAwardRecords
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店ID
 * @param {string} params.id - 第三方消費紀錄ID
 * @returns {import('@/api/instance').Api2Response<Array>} 事件行銷獎勵發放紀錄列表
 */
export const GetMemberExternalTransactionEventPlaybookAwardRecords = async ({
  shopId,
  id,
}) => {
  return await admin2({
    method: 'GET',
    url: `/${shopId}/memberExternalTransaction/${id}/eventPlaybookAwardRecords`,
  })
}
